/**
 * Multiplicative Inverse (Reciprocal) generator (6.PAFR.3.1).
 *
 * Asks for the reciprocal of a fraction, integer, or mixed number. The value is
 * chosen first and the answer is derived with Rational.reciprocal(), so the
 * graded answer is exact and always matches the displayed number.
 *
 * Families:
 *   F1: reciprocal of a proper/improper fraction (a/b -> b/a)
 *   F2: reciprocal of an integer (n -> 1/n)
 *   F3: reciprocal of a mixed number (write as improper first, then invert)
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "multiplicative-inverse" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3";

interface Cfg {
  readonly families: readonly Family[];
  readonly numerMax: number;
  readonly denomMax: number;
  readonly intMax: number;
  readonly wholeMax: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F2"], numerMax: 6, denomMax: 6, intMax: 9, wholeMax: 3 },
  standard: { families: ["F1", "F2", "F3"], numerMax: 9, denomMax: 9, intMax: 12, wholeMax: 4 },
  hard: { families: ["F1", "F2", "F3"], numerMax: 11, denomMax: 12, intMax: 15, wholeMax: 5 },
  challenge: { families: ["F1", "F3"], numerMax: 15, denomMax: 15, intMax: 20, wholeMax: 6 },
};

export class MultiplicativeInverseGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);

      let value: Rational;
      let promptText: string;
      let promptLatex: string;
      let steps: { description: string; latex: string }[];

      switch (family) {
        case "F1": {
          const num = rng.int(2, cfg.numerMax);
          const den = rng.int(2, cfg.denomMax);
          value = rat(num, den);
          // Skip values that reduce to 1 (its own reciprocal, trivial).
          if (value.equals(rat(1))) continue;
          const answer = value.reciprocal();
          promptText = `What is the reciprocal of ${value.toString()}?`;
          promptLatex = `\\text{Find the reciprocal of } ${value.toLatex()}`;
          steps = [
            {
              description: "The reciprocal of a fraction flips the numerator and denominator.",
              latex: `${value.toLatex()} \\rightarrow ${answer.toLatex()}`,
            },
          ];
          return this.build(ctx, topic, family, promptText, promptLatex, answer, steps);
        }
        case "F2": {
          const n = rng.int(2, cfg.intMax);
          value = rat(n);
          const answer = value.reciprocal();
          promptText = `What is the reciprocal of ${value.toString()}?`;
          promptLatex = `\\text{Find the reciprocal of } ${value.toLatex()}`;
          steps = [
            {
              description: "Write the integer as a fraction over 1, then flip it.",
              latex: `${value.toLatex()} = \\frac{${n}}{1} \\rightarrow ${answer.toLatex()}`,
            },
          ];
          return this.build(ctx, topic, family, promptText, promptLatex, answer, steps);
        }
        case "F3": {
          const whole = rng.int(1, cfg.wholeMax);
          const den = rng.int(2, cfg.denomMax);
          const frac = rng.int(1, den - 1);
          const improperNum = whole * den + frac;
          value = rat(improperNum, den);
          const answer = value.reciprocal();
          promptText = `What is the reciprocal of ${whole} ${frac}/${den}?`;
          promptLatex = `\\text{Find the reciprocal of } ${whole}\\frac{${frac}}{${den}}`;
          steps = [
            {
              description: "Convert the mixed number to an improper fraction.",
              latex: `${whole}\\frac{${frac}}{${den}} = ${value.toLatex()}`,
            },
            {
              description: "Flip the numerator and denominator.",
              latex: `${value.toLatex()} \\rightarrow ${answer.toLatex()}`,
            },
          ];
          return this.build(ctx, topic, family, promptText, promptLatex, answer, steps);
        }
      }
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  private build(
    ctx: GenerateContext,
    topic: ReturnType<typeof getTopic>,
    family: Family,
    promptText: string,
    promptLatex: string,
    answer: Rational,
    steps: { description: string; latex: string }[],
  ): Question {
    return {
      id: `${TOPIC}-${ctx.seed}-${family}`,
      generator: TOPIC,
      generatorVersion: VERSION,
      seed: ctx.seed,
      unit: topic.unit,
      topic: TOPIC,
      family,
      difficulty: ctx.difficulty,
      prompt: { text: promptText, latex: promptLatex },
      answerType: "number",
      answer: { kind: "number", value: answer },
      steps,
      hints: [
        { level: 1, text: "The reciprocal is what you multiply by to get 1." },
        { level: 2, text: "Flip the fraction: swap the numerator and denominator." },
      ],
      subskills: topic.subskills,
    };
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "number" ? q.answer.value.key() : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const multiplicativeInverse = new MultiplicativeInverseGenerator();
